/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  keepPreviousData,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';
import {
  type CreateClientInput,
  type CreateTherapistInput,
  type UpdateTherapistInput,
} from '@therapy/shared-validators';

import {
  type Client,
  type ClientsPageParams,
  clientsApi,
  type InviteParentInput,
  type Page,
  type ParentInvite,
  type Therapist,
  type TherapistsPageParams,
  therapistsApi,
} from '../api/resources.js';

const CLIENTS_KEY = ['clients'] as const;
const THERAPISTS_KEY = ['therapists'] as const;
const clientKey = (id: string): readonly unknown[] => ['clients', id];

export function useClients(): UseQueryResult<readonly Client[]> {
  return useQuery({ queryKey: CLIENTS_KEY, queryFn: () => clientsApi.list() });
}

/** Paginated, searchable client list for the Admin management table (P1-1). */
export function useClientsPage(params: ClientsPageParams): UseQueryResult<Page<Client>> {
  return useQuery({
    queryKey: ['clients', 'page', params],
    queryFn: () => clientsApi.listPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useClient(id: string): UseQueryResult<Client> {
  return useQuery({
    queryKey: clientKey(id),
    queryFn: () => clientsApi.getOne(id),
    enabled: id !== '',
  });
}

export function useCreateClient(): UseMutationResult<Client, Error, CreateClientInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientInput) => clientsApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useUpdateClient(): UseMutationResult<
  Client,
  Error,
  { readonly id: string; readonly input: CreateClientInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: CreateClientInput }) =>
      clientsApi.update(vars.id, vars.input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useDeleteClient(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

interface InviteParentVars {
  readonly id: string;
  readonly input: InviteParentInput;
}

export function useInviteParent(): UseMutationResult<ParentInvite, Error, InviteParentVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: InviteParentVars) => clientsApi.inviteParent(vars.id, vars.input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

interface AssignTherapistVars {
  readonly id: string;
  readonly therapistId: string;
}

/** Assign (or re-assign) a client's therapist without booking a session. */
export function useAssignTherapist(): UseMutationResult<void, Error, AssignTherapistVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: AssignTherapistVars) =>
      clientsApi.assignTherapist(vars.id, vars.therapistId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
      void qc.invalidateQueries({ queryKey: clientKey(vars.id) });
    },
  });
}

export function useTherapists(): UseQueryResult<readonly Therapist[]> {
  return useQuery({ queryKey: THERAPISTS_KEY, queryFn: () => therapistsApi.list() });
}

/** Paginated, searchable therapist list for the Admin management table (P1-1). */
export function useTherapistsPage(params: TherapistsPageParams): UseQueryResult<Page<Therapist>> {
  return useQuery({
    queryKey: ['therapists', 'page', params],
    queryFn: () => therapistsApi.listPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateTherapist(): UseMutationResult<Therapist, Error, CreateTherapistInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTherapistInput) => therapistsApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: THERAPISTS_KEY });
    },
  });
}

export function useUpdateTherapist(
  id: string,
): UseMutationResult<Therapist, Error, UpdateTherapistInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTherapistInput) => therapistsApi.update(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: THERAPISTS_KEY });
    },
  });
}
